import { useEffect, useState, type ReactNode } from 'react';
import { missionCoverUrl } from '../api/missions';
import { ca } from '../i18n/ca';
import { hueOf, type Cover } from './missionCovers';
import { TerrainImage } from './TerrainImage';

/**
 * The picture drawn for a mission whose terrain has none: the type's own icon
 * over a wash in the type's own colour.
 *
 * A mission always has a type, and the platform sends the type's Bootstrap icon
 * with every card (`recipeIcon`), so this needs nothing fetched and nothing
 * stored. It is deliberately not a photograph: it says "sabotatge" at a glance
 * in a grid, which a grey box with a world class in it never did.
 */
export function TypeTile({
  recipe,
  icon,
  label,
  className = 'thumb',
}: {
  /** The type's id, which is what the colour comes from. */
  recipe?: string | null;
  /** The type's Bootstrap icon name without the `bi-` prefix. */
  icon?: string | null;
  /** The type's readable name, for the tile's own caption. */
  label?: string | null;
  className?: string;
}) {
  const hue = hueOf(recipe || label || 'ops');
  return (
    <div
      className={`${className} type-tile`}
      style={{
        background: `linear-gradient(150deg, hsl(${hue} 42% 26%), hsl(${(hue + 40) % 360} 38% 14%))`,
      }}
      aria-hidden="true"
    >
      <i className={`bi bi-${icon || 'flag'}`} />
      {label && <span className="type-tile-label">{label}</span>}
    </div>
  );
}

/** How long after a cover failed it is asked for once more, in milliseconds. */
const COVER_RETRY = 4000;

/**
 * The mission's own picture, or what stood there before it existed.
 *
 * The platform makes a cover the first time somebody asks and answers 404 when
 * it is not ready within a moment, or when the terrain has no map to cut. So,
 * as `TerrainImage` does: a picture that did not load is never left as an
 * `<img>`, the fallback stands from the first failure, and the cover is asked
 * for once more off the page before the fallback is what stays.
 */
function OwnCover({
  url,
  className,
  fallback,
}: {
  url: string;
  className: string;
  fallback: ReactNode;
}) {
  const [broken, setBroken] = useState(false);
  const [retried, setRetried] = useState(false);
  const again = `${url}&try=1`;

  useEffect(() => {
    if (!broken || retried) return;
    let gone = false;
    const timer = setTimeout(() => {
      const probe = new Image();
      probe.onload = () => {
        if (gone) return;
        setRetried(true);
        setBroken(false);
      };
      probe.onerror = () => {
        if (!gone) setRetried(true);
      };
      probe.src = again;
    }, COVER_RETRY);
    return () => {
      gone = true;
      clearTimeout(timer);
    };
  }, [broken, retried, again]);

  if (broken) return <>{fallback}</>;
  return (
    <img
      className={className}
      src={retried ? again : url}
      alt=""
      width={480}
      height={270}
      loading="lazy"
      decoding="async"
      onError={() => setBroken(true)}
    />
  );
}

/**
 * The picture a mission is shown with, wherever a list shows one.
 *
 * `AUTO`, which is what every mission has until somebody chooses otherwise, is
 * the mission's own cover when the caller says which mission it is (`code` and
 * `revision`): its terrain's map cut to where the night is played, so twenty
 * missions on one island are twenty pictures. Where there is none it is what
 * it always was — the terrain's picture when the platform has one and the type
 * tile when it has not — so a mission on a world nobody has surveyed still
 * arrives with an image instead of a grey box. `TERRAIN` and `TYPE` force one
 * or the other, which is the choice the designer's Configuració panel offers.
 */
export function MissionCover({
  code,
  revision,
  world,
  worldLabel,
  recipe,
  recipeIcon,
  recipeTitle,
  cover = 'AUTO',
  className = 'thumb',
  size = 'tile',
}: {
  /** The mission and the revision the page holds: together, the address of its own cover. */
  code?: string | null;
  revision?: number | null;
  world?: string | null;
  worldLabel?: string | null;
  recipe?: string | null;
  recipeIcon?: string | null;
  recipeTitle?: string | null;
  cover?: Cover | null;
  className?: string;
  size?: 'tile' | 'card' | 'map';
}) {
  const tile = (
    <TypeTile
      recipe={recipe}
      icon={recipeIcon}
      label={recipeTitle ?? ca.missionUi.cover.noType}
      className={className}
    />
  );
  if (cover === 'TYPE') return tile;
  const terrain = world ? (
    <TerrainImage
      world={world}
      label={worldLabel ?? world}
      className={className}
      size={size}
      fallback={cover === 'TERRAIN' ? undefined : tile}
    />
  ) : (
    tile
  );
  if (cover === 'TERRAIN' || !code || revision === null || revision === undefined) return terrain;
  const url = missionCoverUrl(code, revision);
  // Keyed by its address: another mission, or the same one edited, starts again.
  return <OwnCover key={url} url={url} className={className} fallback={terrain} />;
}
